import React, { useState } from "react";
import styled from "@emotion/styled";
import PreviewSection from "../components/PreviewSection";
import CodeBlock from "../components/CodeBlock";
import {
  flexCenter,
  flexCenterColumn,
  textTruncate,
  visuallyHidden,
  noOutlineOnFocus,
  fadeInAnimation,
  radioButtonCss,
} from "@atlas-int/tokens";
import {
  spacing,
  fontSize,
  fontFamily,
  fontWeight,
  borderRadius,
  border as borderToken,
  brand,
  surface,
  text,
} from "@atlas-int/tokens";

// ─── Styled helpers ──────────────────────────────────────────────────────────

const MixinGroup = styled.div`
  margin-bottom: ${spacing[10]};
`;

const MixinName = styled.h3`
  font-family: ${fontFamily.mono};
  font-size: ${fontSize.base};
  font-weight: ${fontWeight.semibold};
  color: ${text.primary};
  margin: 0 0 ${spacing[2]} 0;
`;

const MixinDescription = styled.p`
  font-size: ${fontSize.sm};
  color: ${text.secondary};
  margin: 0 0 ${spacing[4]} 0;
`;

const DemoArea = styled.div`
  padding: ${spacing[6]};
  border: 1px dashed ${borderToken.default};
  border-radius: ${borderRadius.base};
  margin-bottom: ${spacing[4]};
`;

const ColorBox = styled.div<{ $bg: string }>`
  width: 40px;
  height: 40px;
  border-radius: ${borderRadius.base};
  background-color: ${({ $bg }) => $bg};
`;

// ─── flexCenter demo ─────────────────────────────────────────────────────────

const FlexCenterDemo = styled.div`
  ${flexCenter};
  gap: ${spacing[4]};
  height: 80px;
  background-color: ${surface.muted};
  border-radius: ${borderRadius.base};
`;

// ─── flexCenterColumn demo ───────────────────────────────────────────────────

const FlexCenterColumnDemo = styled.div`
  ${flexCenterColumn};
  gap: ${spacing[4]};
  height: 120px;
  background-color: ${surface.muted};
  border-radius: ${borderRadius.base};
`;

// ─── textTruncate demo ───────────────────────────────────────────────────────

const TruncateDemo = styled.div`
  ${textTruncate};
  width: 200px;
  padding: ${spacing[4]};
  background-color: ${surface.muted};
  border-radius: ${borderRadius.base};
  font-family: ${fontFamily.base};
  font-size: ${fontSize.sm};
`;

// ─── visuallyHidden demo ────────────────────────────────────────────────────

const VisuallyHiddenWrapper = styled.div`
  position: relative;
  padding: ${spacing[4]};
  background-color: ${surface.muted};
  border-radius: ${borderRadius.base};
  font-family: ${fontFamily.base};
  font-size: ${fontSize.sm};
`;

const HiddenLabel = styled.span`
  ${visuallyHidden};
`;

// ─── noOutlineOnFocus demo ──────────────────────────────────────────────────

const FocusButton = styled.button`
  ${noOutlineOnFocus};
  padding: ${spacing[4]} ${spacing[8]};
  border: 1px solid ${borderToken.default};
  border-radius: ${borderRadius.base};
  background-color: ${surface.white};
  font-family: ${fontFamily.base};
  font-size: ${fontSize.sm};
  cursor: pointer;
`;

// ─── fadeInAnimation demo ───────────────────────────────────────────────────

const FadeInBox = styled.div`
  ${fadeInAnimation};
  width: 80px;
  height: 80px;
  background-color: ${brand.dark};
  border-radius: ${borderRadius.base};
`;

// ─── radioButtonCss demo ────────────────────────────────────────────────────

const RadioOption = styled.label<{ $checked: boolean }>`
  ${({ $checked }) => radioButtonCss({ $checked })};
  padding: ${spacing[2]} ${spacing[4]};
  font-family: ${fontFamily.base};
  font-size: ${fontSize.sm};
  cursor: pointer;
  user-select: none;
`;

const RadioRow = styled.div`
  display: flex;
  gap: ${spacing[6]};
`;

// ─── Component ───────────────────────────────────────────────────────────────

const MixinsSection: React.FC = () => {
  const [selectedRadio, setSelectedRadio] = useState("option-a");
  const [fadeKey, setFadeKey] = useState(0);

  return (
    <PreviewSection
      id="mixins"
      title="Mixins / Utilities"
      description="Reusable Emotion CSS snippets from components/styling/mixins.ts. Apply these to styled components to get consistent layout, truncation, accessibility, and animation behaviors."
    >
      {/* flexCenter */}
      <MixinGroup>
        <MixinName>flexCenter</MixinName>
        <MixinDescription>
          Centers children both horizontally and vertically using flexbox.
        </MixinDescription>
        <DemoArea>
          <FlexCenterDemo>
            <ColorBox $bg="#4A90D9" />
            <ColorBox $bg="#7BC47F" />
            <ColorBox $bg="#E8A838" />
          </FlexCenterDemo>
        </DemoArea>
        <CodeBlock
          code={`import { flexCenter } from "@atlas-int/tokens";

const Centered = styled.div\`
  \${flexCenter};
  height: 200px;
\`;`}
        />
      </MixinGroup>

      {/* flexCenterColumn */}
      <MixinGroup>
        <MixinName>flexCenterColumn</MixinName>
        <MixinDescription>
          Centers children vertically in a column layout using flexbox.
        </MixinDescription>
        <DemoArea>
          <FlexCenterColumnDemo>
            <ColorBox $bg="#4A90D9" />
            <ColorBox $bg="#7BC47F" />
          </FlexCenterColumnDemo>
        </DemoArea>
        <CodeBlock
          code={`import { flexCenterColumn } from "@atlas-int/tokens";

const CenteredColumn = styled.div\`
  \${flexCenterColumn};
  height: 300px;
\`;`}
        />
      </MixinGroup>

      {/* textTruncate */}
      <MixinGroup>
        <MixinName>textTruncate</MixinName>
        <MixinDescription>
          Truncates overflowing text with an ellipsis. Requires a fixed width on the container.
        </MixinDescription>
        <DemoArea>
          <TruncateDemo>
            This is a very long piece of text that should be truncated with an ellipsis
          </TruncateDemo>
        </DemoArea>
        <CodeBlock
          code={`import { textTruncate } from "@atlas-int/tokens";

const TruncatedLabel = styled.span\`
  \${textTruncate};
  max-width: 200px;
\`;`}
        />
      </MixinGroup>

      {/* visuallyHidden */}
      <MixinGroup>
        <MixinName>visuallyHidden</MixinName>
        <MixinDescription>
          Hides an element visually while keeping it accessible to screen readers.
          The hidden text below is present in the DOM but not visible.
        </MixinDescription>
        <DemoArea>
          <VisuallyHiddenWrapper>
            <HiddenLabel>This label is visually hidden but screen-reader accessible</HiddenLabel>
            Visible text (inspect the DOM to see the hidden label)
          </VisuallyHiddenWrapper>
        </DemoArea>
        <CodeBlock
          code={`import { visuallyHidden } from "@atlas-int/tokens";

const SrOnlyLabel = styled.label\`
  \${visuallyHidden};
\`;`}
        />
      </MixinGroup>

      {/* noOutlineOnFocus */}
      <MixinGroup>
        <MixinName>noOutlineOnFocus</MixinName>
        <MixinDescription>
          Removes the focus outline for mouse users while preserving it for keyboard users
          via :focus-visible.
        </MixinDescription>
        <DemoArea>
          <FocusButton>Click me (no outline) / Tab to me (outline visible)</FocusButton>
        </DemoArea>
        <CodeBlock
          code={`import { noOutlineOnFocus } from "@atlas-int/tokens";

const CleanButton = styled.button\`
  \${noOutlineOnFocus};
\`;`}
        />
      </MixinGroup>

      {/* fadeInAnimation */}
      <MixinGroup>
        <MixinName>fadeInAnimation</MixinName>
        <MixinDescription>
          Applies a fade-in animation from transparent to opaque using the standard duration token.
        </MixinDescription>
        <DemoArea>
          <FadeInBox key={fadeKey} />
          <button
            onClick={() => setFadeKey((k) => k + 1)}
            style={{ marginTop: 8, cursor: "pointer" }}
          >
            Replay animation
          </button>
        </DemoArea>
        <CodeBlock
          code={`import { fadeInAnimation } from "@atlas-int/tokens";

const FadeInPanel = styled.div\`
  \${fadeInAnimation};
\`;`}
        />
      </MixinGroup>

      {/* radioButtonCss */}
      <MixinGroup>
        <MixinName>radioButtonCss</MixinName>
        <MixinDescription>
          Applies custom radio-button styling with a circle indicator. Accepts a $checked prop
          to toggle the filled dot.
        </MixinDescription>
        <DemoArea>
          <RadioRow>
            <RadioOption
              $checked={selectedRadio === "option-a"}
              onClick={() => setSelectedRadio("option-a")}
            >
              Option A
            </RadioOption>
            <RadioOption
              $checked={selectedRadio === "option-b"}
              onClick={() => setSelectedRadio("option-b")}
            >
              Option B
            </RadioOption>
            <RadioOption
              $checked={selectedRadio === "option-c"}
              onClick={() => setSelectedRadio("option-c")}
            >
              Option C
            </RadioOption>
          </RadioRow>
        </DemoArea>
        <CodeBlock
          code={`import { radioButtonCss } from "@atlas-int/tokens";

const RadioLabel = styled.label<{ $checked: boolean }>\`
  \${({ $checked }) => radioButtonCss({ $checked })};
  padding: 4px 8px;
  cursor: pointer;
\`;`}
        />
      </MixinGroup>
    </PreviewSection>
  );
};

export default MixinsSection;
